import { useContext, useState } from 'react'
import { FileText, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import type { Review } from '../../types'
import ReviewForm from './ReviewForm'
import { AuthContext } from '../../contexts/AuthContext'
import { shortDate } from '../../lib/shortDate'
import { reviewerLabel } from './reviewerLabel'

interface ReviewCardProps {
  review: Review
  type: 'sent' | 'received'
}

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  pending: { icon: <Clock size={14} />, color: 'text-yellow-600 bg-yellow-50' },
  queued: { icon: <AlertCircle size={14} />, color: 'text-iflow-600 bg-iflow-50' },
  completed: { icon: <CheckCircle size={14} />, color: 'text-green-600 bg-green-50' },
  cancelled: { icon: <XCircle size={14} />, color: 'text-gray-500 bg-sky-500/10' },
}

const DECISION_COLORS: Record<string, string> = {
  approved: 'text-green-600',
  rejected: 'text-red-600',
  changes_requested: 'text-yellow-600',
}

export default function ReviewCard({ review, type }: ReviewCardProps) {
  const [showForm, setShowForm] = useState(false)
  // Optional on purpose: a card rendered outside a provider still shows "Unknown reviewer".
  const user = useContext(AuthContext)?.user
  const statusConfig = STATUS_CONFIG[review.status] || STATUS_CONFIG.pending

  return (
    <div className="light-card p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-500/5 flex items-center justify-center">
            <FileText size={18} className="text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Review #{review.review_id.slice(0, 8)}</p>
            <p className="text-xs text-gray-500">
              {review.review_type === 'sequential' ? 'Sequential' : 'Parallel'} review
              {review.sequence_order > 0 && ` (step ${review.sequence_order})`}
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
          {statusConfig.icon} {review.status}
        </span>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <span>Reviewer: {reviewerLabel(review, user)}</span>
        <span>Created: {shortDate(review.created_at) ?? 'Unknown date'}</span>
      </div>

      {/* Decision display */}
      {review.decision && (
        <div className="flex items-center justify-between text-xs mb-3 p-2 bg-sky-500/5 rounded-lg">
          <span className="text-gray-600">Decision</span>
          <span className={`capitalize font-medium ${DECISION_COLORS[review.decision] || 'text-gray-500'}`}>
            {review.decision.replace(/_/g, ' ')}
          </span>
        </div>
      )}

      {review.feedback && (
        <p className="text-xs text-gray-500 mb-3 italic">"{review.feedback}"</p>
      )}

      {review.message && (
        <p className="text-xs text-gray-500 mb-3">{review.message}</p>
      )}

      {type === 'received' && review.status === 'pending' && (
        <button
          onClick={() => setShowForm(!showForm)}
          className="text-sm text-iflow-600 font-medium hover:text-iflow-700"
        >
          {showForm ? 'Hide form' : 'Submit Review'}
        </button>
      )}

      {showForm && <ReviewForm reviewId={review.review_id} onSubmitted={() => setShowForm(false)} />}
    </div>
  )
}
